import React, { ReactElement, useRef, useState, useCallback } from 'react'
import { useSprings, animated } from '@react-spring/web'
import { useDrag } from '@use-gesture/react'
import Grid from '../Grid'
import SnapListProps from './SnapListProps'

type handleItemsType = {
    mx: number
    xDir: number
    down: boolean
    showNext: boolean
}

type SpringConfig = {
    duration?: number
}

type SpringValue = {
    x?: number
    display?: 'flex' | 'none'
    config?: SpringConfig
}

const getNearestIndicies = (startIndex: number, maxLength: number): number[] => {
    const prevIndex = startIndex - 1
    const nextIndex = startIndex + 1
    return [
        prevIndex >= 0 ? prevIndex : maxLength - 1,
        startIndex,
        nextIndex < maxLength ? nextIndex : 0,
    ]
}

const SnapList = <T extends {}>({
    innerRef,
    data,
    width,
    startIndex = 0,
    onItemPress,
    onIndexChanged,
    keyExtractor,
    renderItem,
}: SnapListProps<T>): ReactElement | null => {
    const initial = useRef(true)
    const containerRef = React.useRef<(ReactElement & HTMLDivElement) | null>(null)
    const [items] = useState(data.length === 2 ? [...data, ...data] : data)
    const [containerWidth, setContainerWidth] = React.useState<number | undefined>(width)
    const index = useRef(startIndex)

    const setItemsForMidItemSelection = useCallback(
        (itemIndex: number, mx: number, xDir: number, down: boolean): SpringValue => {
            if (!containerWidth || itemIndex > index.current + 1 || itemIndex < index.current - 1) {
                return { display: 'none' }
            }
            const x = -containerWidth + (down ? mx : 0)
            return { x, display: 'flex', config: { duration: down ? 0 : undefined } }
        },
        [containerWidth]
    )

    const setItemsForFirstItemSelection = useCallback(
        (itemIndex: number, mx: number, xDir: number, down: boolean): SpringValue => {
            if (
                !containerWidth ||
                (itemIndex !== items.length - 1 && itemIndex !== 1 && itemIndex !== 0)
            ) {
                return { display: 'none' }
            }
            if (itemIndex === items.length - 1) {
                const x = -containerWidth * 3 + (down ? mx : 0)
                return { x, display: 'flex', config: { duration: down ? 0 : undefined } }
            } else if (itemIndex === 1) {
                const x = down ? mx : 0
                return { x, display: 'flex', config: { duration: down ? 0 : undefined } }
            }

            const x = down ? mx : 0
            return { x, display: 'flex', config: { duration: down ? 0 : undefined } }
        },
        [containerWidth, items]
    )

    const setItemsForLastItemSelection = useCallback(
        (itemIndex: number, mx: number, xDir: number, down: boolean): SpringValue => {
            if (
                !containerWidth ||
                (itemIndex !== items.length - 1 &&
                    itemIndex !== items.length - 2 &&
                    itemIndex !== 0)
            ) {
                return { display: 'none' }
            }
            if (itemIndex === items.length - 2) {
                const x = -containerWidth * 2 + (down ? mx : 0)
                return { x, display: 'flex', config: { duration: down ? 0 : undefined } }
            } else if (itemIndex === 0) {
                const x = containerWidth + (down ? mx : 0)
                return { x, display: 'flex', config: { duration: down ? 0 : undefined } }
            }

            const x = -containerWidth * 2 + (down ? mx : 0)
            return { x, display: 'flex', config: { duration: down ? 0 : undefined } }
        },
        [containerWidth, items]
    )

    const initItems = useCallback(
        (i: number): SpringValue => {
            if (index.current === 0) {
                return setItemsForFirstItemSelection(i, 0, 0, true)
            } else if (index.current === items.length - 1) {
                return setItemsForLastItemSelection(i, 0, 0, true)
            }
            return setItemsForMidItemSelection(i, 0, 0, true)
        },
        [
            setItemsForFirstItemSelection,
            setItemsForLastItemSelection,
            setItemsForMidItemSelection,
            items,
        ]
    )

    const [props, api] = useSprings(items.length, i => ({
        x: 0,
        display: getNearestIndicies(startIndex, items.length).includes(i) ? 'flex' : 'none',
    }))

    const handleItemsForMidItemSelection = useCallback(
        ({ mx, xDir, showNext, down }: handleItemsType): void => {
            if (showNext) {
                api.start((i: number) => {
                    if (!containerWidth || i > index.current + 1 || i < index.current - 1) {
                        return { display: 'none' }
                    }
                    const x = xDir < 0 ? -containerWidth * 2 : 0
                    return {
                        x,
                        display: 'flex',
                        config: { duration: undefined },
                    }
                })
                index.current = xDir < 0 ? index.current + 1 : index.current - 1
                onIndexChanged && onIndexChanged(index.current)
            } else {
                api.start((i: number) => setItemsForMidItemSelection(i, mx, xDir, down))
            }
        },
        [containerWidth, setItemsForMidItemSelection, api, onIndexChanged]
    )

    const handleItemsForFirstItemSelection = useCallback(
        ({ mx, xDir, showNext, down }: handleItemsType): void => {
            if (showNext) {
                const showRightItem = xDir < 0
                api.start((i: number) => {
                    if (!containerWidth || (i !== items.length - 1 && i !== 1 && i !== 0)) {
                        return { display: 'none' }
                    }
                    if (i === items.length - 1) {
                        const x = showRightItem ? -containerWidth * 3 : -containerWidth * 2
                        return { x, display: 'flex', config: { duration: undefined } }
                    } else if (i === 1) {
                        const x = showRightItem ? -containerWidth : 0
                        return { x, display: 'flex', config: { duration: undefined } }
                    }

                    const x = showRightItem ? -containerWidth : containerWidth
                    return {
                        x,
                        display: 'flex',
                        config: { duration: undefined },
                    }
                })
                index.current = showRightItem ? 1 : items.length - 1
                onIndexChanged && onIndexChanged(index.current)
            } else {
                api.start((i: number) => setItemsForFirstItemSelection(i, mx, xDir, down))
            }
        },
        [containerWidth, setItemsForFirstItemSelection, api, onIndexChanged, items]
    )

    const handleItemsForLastItemSelection = useCallback(
        ({ mx, xDir, showNext, down }: handleItemsType): void => {
            if (showNext) {
                const showRightItem = xDir < 0
                api.start((i: number) => {
                    if (
                        !containerWidth ||
                        (i !== items.length - 1 && i !== items.length - 2 && i !== 0)
                    ) {
                        return { display: 'none' }
                    }
                    if (i === items.length - 2) {
                        const x = showRightItem ? -containerWidth * 2 : -containerWidth
                        return { x, display: 'flex', config: { duration: undefined } }
                    } else if (i === 0) {
                        const x = showRightItem ? 0 : containerWidth
                        return { x, display: 'flex', config: { duration: undefined } }
                    }

                    const x = showRightItem ? -containerWidth * 3 : -containerWidth
                    return {
                        x,
                        display: 'flex',
                        config: { duration: undefined },
                    }
                })
                index.current = showRightItem ? 0 : items.length - 2
                onIndexChanged && onIndexChanged(index.current)
            } else {
                api.start((i: number) => setItemsForLastItemSelection(i, mx, xDir, down))
            }
        },
        [containerWidth, setItemsForLastItemSelection, api, onIndexChanged, items]
    )

    const handleItems = useCallback(
        ({ mx, xDir, showNext, down }: handleItemsType): void => {
            if (index.current === 0) {
                handleItemsForFirstItemSelection({ mx, xDir, down, showNext })
            } else if (index.current > 0 && index.current < items.length - 1) {
                handleItemsForMidItemSelection({ mx, xDir, down, showNext })
            } else {
                handleItemsForLastItemSelection({ mx, xDir, down, showNext })
            }
        },
        [
            handleItemsForFirstItemSelection,
            handleItemsForMidItemSelection,
            handleItemsForLastItemSelection,
            items,
        ]
    )

    const bind = useDrag(
        ({ movement: [mx], direction: [xDir], distance, down, tap }) => {
            if (tap) {
                onItemPress && onItemPress(index.current)
            } else {
                const showNext = !down && distance[0] > Math.min((containerWidth || 1) / 3, 100)
                handleItems({ mx, xDir, down, showNext })
            }
            return false
        },
        { filterTaps: true, axis: 'x' }
    )

    const setNewContainerWidth = useCallback(() => {
        const newContainerWidth = containerRef.current?.getBoundingClientRect().width || 0
        if (newContainerWidth !== containerWidth) {
            setContainerWidth(newContainerWidth)
        }
    }, [containerWidth])

    const renderPage = useCallback(
        (item: T, index: number): ReactElement => {
            const itemToRender = renderItem({
                item,
                index,
            })
            return itemToRender?.component
        },
        [renderItem]
    )

    React.useEffect(() => {
        if (!width) {
            if (initial.current) {
                initial.current = false
                setNewContainerWidth()
            }
            window.addEventListener('resize', setNewContainerWidth)
        }
        return (): void => {
            window.removeEventListener('resize', setNewContainerWidth)
        }
    }, [setNewContainerWidth, width])

    React.useEffect(() => {
        if (containerWidth) {
            api.start(initItems)
        }
        if (innerRef) {
            innerRef.current = {
                showPrevious: (): number => {
                    const newIndex = index.current === 0 ? items.length - 1 : index.current - 1
                    api.start(initItems)[newIndex].then(() => {
                        handleItems({ mx: 0, xDir: 1, down: false, showNext: true })
                    })
                    return newIndex
                },
                showNext: (): number => {
                    const newIndex = index.current === items.length - 1 ? 0 : index.current + 1
                    api.start(initItems)[newIndex].then(() => {
                        handleItems({ mx: 0, xDir: -1, down: false, showNext: true })
                    })
                    return newIndex
                },
            }
        }
    }, [containerWidth, api, initItems, innerRef, handleItems, items.length])

    return items.length ? (
        <Grid
            ref={containerRef}
            noExpand
            height="100%"
            width={width}
            overflow="hidden"
            pointerEvents="box-none"
        >
            {items.length === 1 ? (
                renderPage(items[0], 0)
            ) : (
                <Grid noExpand height="100%" horizontal overflow="hidden">
                    {props.map(({ x, display }, index) => {
                        const item = items[index]
                        return (
                            <animated.div
                                {...bind()}
                                key={keyExtractor(item, index)}
                                style={{
                                    userSelect: 'none',
                                    touchAction: 'none',
                                    willChange: 'transform',
                                    flexDirection: 'row',
                                    display,
                                    translateX: x,
                                    width: '100%',
                                    minWidth: '100%',
                                }}
                            >
                                {renderPage(item, index)}
                            </animated.div>
                        )
                    })}
                </Grid>
            )}
        </Grid>
    ) : null
}

export default SnapList
